import apiService from './api.service';
import type { ApiResponse } from '@/types';
import type { RejectionPayload } from './rejection.service';

function isQuotationPayload(v: unknown): v is Quotation {
  return !!v && typeof v === 'object' && 'id' in v && 'quotation_number' in v;
}

function normalizeQuotation(q: Quotation | undefined | null): Quotation | undefined {
  if (!q) return undefined;
  return { ...q, items: Array.isArray(q.items) ? q.items : [] };
}

/** Supports `{ success, data: Quotation }` or a bare quotation object. */
function peelQuotation(body: unknown): Quotation | undefined {
  if (!body || typeof body !== 'object') return undefined;
  if (isQuotationPayload(body)) return body as Quotation;
  const wrap = body as ApiResponse<Quotation>;
  if (wrap.data && isQuotationPayload(wrap.data)) return wrap.data;
  return undefined;
}

function peelQuotationList(body: unknown): Quotation[] {
  if (!body) return [];
  let rows: unknown[] = [];
  if (Array.isArray(body)) {
    rows = body;
  } else {
    const w = body as ApiResponse<Quotation[]>;
    if (Array.isArray(w.data)) {
      rows = w.data;
    } else {
      return [];
    }
  }
  return rows
    .map((row) => normalizeQuotation(isQuotationPayload(row) ? (row as Quotation) : peelQuotation(row)))
    .filter((x): x is Quotation => x !== undefined);
}

export interface QuotationRequest {
  quotation_name: string;
  inquiry_reference?: string;
  sap_number?: string;
  customer_id?: string;
  customer_name: string;
  customer_address?: string;
  customer_contact?: string;
  project_name?: string;
  project_id?: string;
  department_id?: string;
  opportunity_id?: string;
  revenue_service_type_id?: string;
  estimation_id?: string;
  currency?: string;
  quotation_date?: string;
  valid_until?: string;
  validity_days?: number;
  payment_terms?: string;
  delivery_terms?: string;
  advance_payment_percentage?: number;
  vat_applicable?: boolean;
  vat_percentage?: number;
  discount_percentage?: number;
  notes?: string;
  terms_and_conditions?: string;
  special_instructions?: string;
  items: QuotationItemRequest[];
}

export interface QuotationItemRequest {
  item_code?: string;
  item_description: string;
  revision?: string | null;
  item_category?: string;
  quantity: number;
  uom?: string;
  unit_price: number;
  discount_percentage?: number;
  vat_applicable?: boolean;
  vat_percentage?: number;
  notes?: string;
  specifications?: string;
  delivery_days?: number;
  revenue_service_type_id?: string;
  /** Persisted line id — send back on edit so the server keeps estimation links and cost. */
  id?: string;
  trading_rental_estimation_line_id?: string;
  line_type?: string;
  rental_period_type?: string | null;
  rental_start_date?: string | null;
  rental_end_date?: string | null;
  rental_duration?: number;
  rental_rate?: number;
  security_deposit?: number;
  mobilization_charges?: number;
  demobilization_charges?: number;
  operator_charges?: number;
  transport_charges?: number;
  damage_charges?: number;
}

export interface Quotation {
  id: string;
  company_id: string;
  quotation_number: string;
  revision_number: number;
  opportunity_id?: string;
  revenue_service_type_id?: string;
  estimation_id?: string;
  trading_rental_estimation_id?: string;
  source_estimation_type?: string;
  boq_id?: string;
  /** Resolved on read — use for display instead of raw FK UUIDs. */
  opportunity_summary?: { id: string; opportunity_number: string; opportunity_name: string };
  estimation_summary?: { id: string; estimation_number: string; estimation_name?: string };
  boq_summary?: { id: string; boq_number: string; boq_name?: string };
  trading_rental_estimation_summary?: { id: string; estimation_number: string };
  quotation_name: string;
  inquiry_reference?: string | null;
  sap_number?: string | null;
  customer_id?: string;
  customer_name: string;
  customer_address?: string;
  customer_contact?: string;
  customer_email?: string;
  customer_phone?: string;
  project_name?: string;
  project_id?: string;
  /** Live from Projects master on API reads (not stored on quotations row). */
  project_number?: string | null;
  project_summary?: { id: string; project_number: string; project_name: string };
  department_id?: string | null;
  subtotal: number;
  discount_percentage: number;
  discount_amount: number;
  total_after_discount: number;
  vat_applicable: boolean;
  vat_percentage: number;
  vat_amount: number;
  grand_total: number;
  currency: string;
  payment_terms?: string;
  advance_payment_percentage: number;
  delivery_terms?: string;
  quotation_date: string;
  valid_until?: string;
  validity_days: number;
  status: string;
  sent_date?: string;
  acceptance_date?: string;
  rejection_date?: string;
  rejection_reason?: string;
  approval_status: string;
  approval_instance_id?: string | null;
  approved_by?: string;
  approved_date?: string;
  parent_quotation_id?: string;
  is_latest_revision: boolean;
  is_current_submitted?: boolean;
  superseded_at?: string | null;
  superseded_by_quotation_id?: string | null;
  revision_reason?: string;
  root_quotation_id?: string | null;
  revision_type?: string | null;
  revision_notes?: string | null;
  customer_signature_url?: string;
  customer_signed_date?: string;
  prepared_by: string;
  sales_person?: string;
  preparedByUser?: {
    id?: string;
    first_name?: string | null;
    last_name?: string | null;
    username?: string | null;
    email?: string | null;
  } | null;
  salesPersonUser?: {
    id?: string;
    first_name?: string | null;
    last_name?: string | null;
    username?: string | null;
    email?: string | null;
  } | null;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  terms_and_conditions?: string;
  notes?: string;
  special_instructions?: string;
  is_converted_to_sales_order: boolean;
  sales_order_id?: string;
  /** Computed: awaiting_customer_order | won_pr_awaiting_so | converted_to_so | other */
  commercial_stage?: string;
  commercial_stage_label?: string;
  has_active_production_request?: boolean;
  is_converted_to_project: boolean;
  pdf_file_url?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  items?: QuotationItem[];
  overheads?: QuotationOverhead[];
  profitability_snapshot?: QuotationProfitabilitySnapshot | null;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}

export interface QuotationItem {
  id: string;
  quotation_id: string;
  boq_line_item_id?: string;
  trading_rental_estimation_line_id?: string;
  line_type?: string;
  rental_period_type?: string | null;
  rental_start_date?: string;
  rental_end_date?: string;
  rental_duration?: number;
  rental_rate?: number;
  security_deposit?: number;
  mobilization_charges?: number;
  demobilization_charges?: number;
  operator_charges?: number;
  transport_charges?: number;
  damage_charges?: number;
  line_number: number;
  item_code?: string;
  item_description: string;
  revision?: string | null;
  item_category?: string;
  quantity: number;
  uom?: string;
  unit_price: number;
  line_total: number;
  base_cost_amount?: number;
  allocated_overhead_amount?: number;
  loaded_cost_amount?: number;
  margin_percentage_snapshot?: number;
  margin_amount_snapshot?: number;
  discount_percentage: number;
  discount_amount: number;
  net_amount: number;
  vat_applicable: boolean;
  vat_percentage: number;
  vat_amount: number;
  notes?: string;
  specifications?: string;
  delivery_days?: number;
  revenue_service_type_id?: string;
  created_at: string;
  updated_at: string;
}

export interface QuotationOverhead {
  id: string;
  overhead_type: string;
  description?: string | null;
  remarks?: string | null;
  currency: string;
  original_amount: number;
  amount: number;
  line_number: number;
}

export interface QuotationProfitabilitySnapshot {
  material_cost: number;
  rental_cost: number;
  total_overhead: number;
  loaded_total_cost: number;
  quotation_value: number;
  gross_profit: number;
  gross_profit_percentage: number;
  margin_percentage: number;
  trading_rental_estimation_id?: string | null;
}

export type QuotationPrintMode =
  | 'customer'
  | 'customer_rental_compact'
  /** Line items / specs / qty for operations — no unit prices, costs, or commercial totals. */
  | 'technical'
  | 'internal'
  | 'internal_breakdown';
export type CustomerOverheadPrintOption = 'none' | 'summary' | 'breakdown';

export type ReconciliationDecision =
  | 'ACCEPT_ESTIMATE_CHANGE'
  | 'KEEP_QUOTATION_VALUE'
  | 'ADD'
  | 'IGNORE'
  | 'REMOVE'
  | 'REVIEW';

export interface EstimateReconciliationPreview {
  quotation_id: string;
  quotation_status: string;
  source_estimate: {
    id: string;
    estimation_number: string;
    revision_number: number;
    total_cost: number;
  };
  target_estimate: {
    id: string;
    estimation_number: string;
    revision_number: number;
    total_cost: number;
  };
  estimate_comparison: {
    summary: {
      cost_delta: number;
      added_count: number;
      removed_count: number;
      quantity_changed_count: number;
      cost_changed_count: number;
    };
  };
  changes: Array<{
    change_id: string;
    change_type: string;
    requires_decision: boolean;
    default_decision: ReconciliationDecision;
    allowed_decisions: ReconciliationDecision[];
    commercially_modified: boolean;
    line_provenance: string | null;
    estimate_change: {
      current_line?: { item_description?: string } | null;
      previous_line?: { item_description?: string } | null;
    };
  }>;
  commercial_impact: {
    label: 'PROJECTED';
    current_cost: number;
    current_sell: number;
    projected_cost: number;
    projected_sell: number;
    projected_margin_percent: number | null;
  };
  unresolved_decisions: number;
  warnings: string[];
  preview_hash: string;
  quotation_updated_at: string;
  newer_estimate_available: { id: string; revision_number: number } | null;
}

export type SourceSyncLineValues = {
  item_code?: string | null;
  item_description?: string | null;
  uom?: string | null;
  quantity?: number | string | null;
  unit_price?: number | string | null;
  discount_percentage?: number | string | null;
  rental_rate?: number | string | null;
  rental_duration?: number | string | null;
  line_total?: number | string | null;
  net_amount?: number | string | null;
  loaded_cost_amount?: number | string | null;
  [key: string]: unknown;
};

export interface SourceSyncChange {
  change_id: string;
  change_type: string;
  quotation_item_id: string | null;
  source_line_id: string | null;
  matched_by: 'link' | 'code_or_position' | null;
  legacy: boolean;
  price_basis: 'source' | 'markup_kept' | 'quotation' | null;
  field_deltas: Array<{ field: string; quotation: unknown; source: unknown }>;
  quotation_line: (SourceSyncLineValues & { id: string; line_number: number }) | null;
  source_line: { id: string; line_number: number; values: SourceSyncLineValues } | null;
  commercially_modified: boolean;
  line_provenance: string | null;
  requires_decision: boolean;
  default_decision: ReconciliationDecision;
  allowed_decisions: ReconciliationDecision[];
}

export interface SourceSyncPreview {
  quotation_id: string;
  quotation_number: string;
  revision_number: number;
  quotation_status: string;
  quotation_updated_at: string;
  source: {
    kind: 'boq' | 'trading_rental';
    id: string;
    number: string;
    name: string | null;
    status: string;
    currency: string | null;
    customer_name: string | null;
    project_name: string | null;
    updated_at: string | null;
  };
  eligibility: {
    can_apply: boolean;
    reason: string | null;
    needs_revision: boolean;
    can_revise: boolean;
    revise_reason: string | null;
  };
  blocking_issues: string[];
  warnings: string[];
  changes: SourceSyncChange[];
  summary: {
    added: number;
    removed: number;
    quantity: number;
    price: number;
    description: number;
    specification: number;
    cost_only: number;
    unchanged: number;
    manual_lines_kept: number;
    relinked: number;
    commercial_changes: number;
  };
  commercial_impact: {
    current_sell: number;
    projected_sell: number;
    current_cost: number;
    projected_cost: number;
    current_margin_percent: number | null;
    projected_margin_percent: number | null;
  };
  unresolved_decisions: number;
  last_synced_at: string | null;
  preview_hash: string;
}

class QuotationService {
  private baseURL = '/crm/quotations';

  /**
   * Get all quotations
   */
  async getAll(filters?: {
    status?: string;
    opportunity_id?: string;
    customer_id?: string;
    boq_id?: string;
    trading_rental_estimation_id?: string;
    is_latest_revision?: boolean;
    sales_team_id?: string;
    search?: string;
    commercial_stage?: string;
  }): Promise<Quotation[]> {
    const body = await apiService.get<Quotation[]>(this.baseURL, { params: filters });
    return peelQuotationList(body as unknown);
  }

  /**
   * Get quotation by ID
   */
  async getById(id: string): Promise<Quotation | undefined> {
    const body = await apiService.get<Quotation>(`${this.baseURL}/${id}`);
    return normalizeQuotation(peelQuotation(body as unknown));
  }

  /**
   * Create new quotation
   */
  async create(data: QuotationRequest) {
    return await apiService.post<Quotation>(this.baseURL, data);
  }

  /**
   * Update quotation
   */
  async update(id: string, data: Partial<QuotationRequest>) {
    return await apiService.put<Quotation>(`${this.baseURL}/${id}`, data);
  }

  /**
   * Delete quotation
   */
  async delete(id: string) {
    return await apiService.delete(`${this.baseURL}/${id}`);
  }

  async submitForUniversalApproval(id: string) {
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/submit-for-universal-approval`, {});
  }

  /**
   * Approve quotation
   */
  async approve(id: string, remarks?: string) {
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/approve`, { remarks: remarks ?? undefined });
  }

  async rejectUniversalApproval(id: string, payload: RejectionPayload) {
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/reject-universal-approval`, payload);
  }

  /**
   * Send quotation to customer
   */
  async send(id: string) {
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/send`, {});
  }

  /**
   * Accept quotation
   */
  async accept(id: string, customerSignatureUrl?: string) {
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/accept`, {
      customer_signature_url: customerSignatureUrl
    });
  }

  /**
   * Reject quotation
   */
  async reject(id: string, rejectionReason: string) {
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/reject`, {
      rejection_reason: rejectionReason
    });
  }

  async revokeApproval(id: string, reason: string) {
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/revoke-approval`, { reason });
  }

  /**
   * Create quotation revision
   */
  async createRevision(
    id: string,
    revisionReason: string,
    revisedData?: Partial<QuotationRequest> & {
      revision_type?: string | null;
      revision_notes?: string | null;
      estimate_source?: 'keep' | 'latest_approved' | 'selected';
      selected_estimation_id?: string | null;
    }
  ) {
    const {
      revision_type,
      revision_notes,
      estimate_source,
      selected_estimation_id,
      ...rest
    } = revisedData || {};
    return await apiService.post<Quotation>(`${this.baseURL}/${id}/revise`, {
      revision_reason: revisionReason,
      revision_type,
      revision_notes,
      estimate_source,
      selected_estimation_id,
      ...rest,
    });
  }

  async listRevisionFamily(id: string): Promise<Quotation[]> {
    const body = await apiService.get<Quotation[]>(`${this.baseURL}/${id}/revisions`);
    return peelQuotationList(body as unknown);
  }

  async listSubmissionHistory(id: string): Promise<
    Array<{
      id: string;
      quotation_number: string;
      revision_number: number;
      display_revision: string;
      status: string;
      is_current_submitted: boolean;
      is_latest_revision: boolean;
      sent_date: string | null;
      acceptance_date: string | null;
      rejection_date: string | null;
      superseded_at: string | null;
      superseded_by_quotation_id: string | null;
      grand_total: number;
      currency: string;
      role: string;
    }>
  > {
    const body = await apiService.get(`${this.baseURL}/${id}/submission-history`);
    const wrap = body as { data?: unknown };
    const data = wrap?.data ?? body;
    return Array.isArray(data) ? data : [];
  }

  async compareRevisions(id: string, withId?: string) {
    const body = await apiService.get(`${this.baseURL}/${id}/revisions/compare`, {
      params: withId ? { withId } : undefined,
    });
    const wrap = body as { data?: unknown };
    return wrap?.data ?? body;
  }

  async getEstimateReconciliation(id: string, targetEstimationId: string): Promise<EstimateReconciliationPreview> {
    const body = await apiService.get(`${this.baseURL}/${id}/estimate-reconciliation`, {
      params: { targetEstimationId },
    });
    const wrap = body as { data?: EstimateReconciliationPreview };
    return (wrap?.data ?? body) as EstimateReconciliationPreview;
  }

  async previewEstimateReconciliation(
    id: string,
    payload: {
      targetEstimationId: string;
      decisions?: Array<{ change_id: string; decision: ReconciliationDecision }>;
    }
  ): Promise<EstimateReconciliationPreview> {
    const body = await apiService.post(`${this.baseURL}/${id}/estimate-reconciliation/preview`, payload);
    const wrap = body as { data?: EstimateReconciliationPreview };
    return (wrap?.data ?? body) as EstimateReconciliationPreview;
  }

  async applyEstimateReconciliation(
    id: string,
    payload: {
      targetEstimationId: string;
      previewHash: string;
      expectedQuotationUpdatedAt: string;
      decisions: Array<{ change_id: string; decision: ReconciliationDecision }>;
    }
  ) {
    const body = await apiService.post(`${this.baseURL}/${id}/estimate-reconciliation/apply`, payload);
    const wrap = body as { data?: unknown };
    return wrap?.data ?? body;
  }

  /** Compare a BOQ / Trading-Rental quotation with the current state of its source estimation. */
  async getSourceSync(id: string): Promise<SourceSyncPreview> {
    const body = await apiService.get(`${this.baseURL}/${id}/source-sync`);
    const wrap = body as { data?: SourceSyncPreview };
    return (wrap?.data ?? body) as SourceSyncPreview;
  }

  async previewSourceSync(
    id: string,
    decisions: Array<{ change_id: string; decision: ReconciliationDecision }>
  ): Promise<SourceSyncPreview> {
    const body = await apiService.post(`${this.baseURL}/${id}/source-sync/preview`, { decisions });
    const wrap = body as { data?: SourceSyncPreview };
    return (wrap?.data ?? body) as SourceSyncPreview;
  }

  async applySourceSync(
    id: string,
    payload: {
      previewHash: string;
      expectedQuotationUpdatedAt: string;
      decisions: Array<{ change_id: string; decision: ReconciliationDecision }>;
    }
  ): Promise<{ snapshot_id: string; summary: Record<string, number> }> {
    const body = await apiService.post(`${this.baseURL}/${id}/source-sync/apply`, payload);
    const wrap = body as { data?: { snapshot_id: string; summary: Record<string, number> } };
    return (wrap?.data ?? body) as { snapshot_id: string; summary: Record<string, number> };
  }

  /**
   * Convert quotation to sales order
   */
  async convertToSalesOrder(
    quotationId: string,
    departmentId?: string
  ): Promise<{ quotation_id: string; sales_order_id: string }> {
    const payload: { department_id?: string } = {};
    if (departmentId?.trim()) payload.department_id = departmentId.trim();
    const response = await apiService.post(`${this.baseURL}/${quotationId}/convert-to-sales-order`, payload);
    return response.data;
  }

  /**
   * Create quotation from BOQ
   */
  async createFromBOQ(
    boqId: string,
    options?: {
      opportunityId?: string;
      selectedLineIds?: string[];
      revenueServiceTypeId?: string;
      departmentId?: string;
    }
  ) {
    const params =
      options?.opportunityId != null && options.opportunityId !== ''
        ? { opportunity_id: options.opportunityId }
        : undefined;
    const payload: {
      selectedLineIds?: string[];
      revenueServiceTypeId?: string;
      department_id?: string;
    } = {};
    if (Array.isArray(options?.selectedLineIds) && options.selectedLineIds.length > 0) {
      payload.selectedLineIds = options.selectedLineIds;
    }
    if (options?.revenueServiceTypeId?.trim()) {
      payload.revenueServiceTypeId = options.revenueServiceTypeId.trim();
    }
    if (options?.departmentId?.trim()) {
      payload.department_id = options.departmentId.trim();
    }
    return await apiService.post<Quotation>(`${this.baseURL}/from-boq/${boqId}`, payload, { params });
  }
}

export default new QuotationService();
